import { rgba, type MantineTheme } from '@mantine/core';
import type { CSSProperties } from 'react';

type ColorScheme = 'light' | 'dark';
type ChatColors = {
  primary: string;
  primaryStrong: string;
  online: string;
};

export const CHAT_LAYOUT = {
  drawerWidth: {
    compact: '100%',
    conversation: 500,
    inbox: 420,
  },
  overlay: {
    backgroundOpacity: 0.28,
    blur: 3,
  },
} as const;

const themedColor = (
  theme: MantineTheme,
  colorScheme: ColorScheme,
  color:
    | 'surface'
    | 'surface-elevated'
    | 'border'
    | 'primary'
    | 'secondary'
    | 'green'
): string => theme.colors[`${color}-${colorScheme}`][0];

export const getChatColors = (
  theme: MantineTheme,
  colorScheme: ColorScheme
): ChatColors => ({
  primary: themedColor(theme, colorScheme, 'primary'),
  primaryStrong: themedColor(theme, colorScheme, 'secondary'),
  online: themedColor(theme, colorScheme, 'green'),
});

export const getChatStyles = (
  theme: MantineTheme,
  colorScheme: ColorScheme,
  compact: boolean
): Record<string, CSSProperties> => {
  const surface = themedColor(theme, colorScheme, 'surface');
  const elevatedSurface = themedColor(theme, colorScheme, 'surface-elevated');
  const border = themedColor(theme, colorScheme, 'border');
  const { primary } = getChatColors(theme, colorScheme);
  const text = theme.colors[`gray-${colorScheme}`][0];
  const primarySoft = rgba(primary, colorScheme === 'dark' ? 0.16 : 0.09);
  const outgoingMessageBackground =
    colorScheme === 'dark' ? theme.colors['secondary-light'][0] : primary;
  const horizontalPadding = compact ? theme.spacing.md : theme.spacing.lg;

  return {
    drawerContent: {
      width: compact ? '100%' : undefined,
      maxWidth: '100%',
      background: elevatedSurface,
      boxShadow: theme.shadows.xl,
    },
    drawerHeader: {
      minHeight: 76,
      padding: `${theme.spacing.md} ${horizontalPadding}`,
      borderBottom: `1px solid ${border}`,
      background: rgba(elevatedSurface, 0.94),
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    },
    drawerTitle: {
      flex: 1,
      minWidth: 0,
    },
    drawerBody: {
      height: 'calc(100dvh - 76px)',
      padding: 0,
    },
    fullHeightView: {
      height: '100%',
      minHeight: 0,
    },
    conversationNav: {
      flex: '0 0 auto',
      padding: `${theme.spacing.sm} ${horizontalPadding}`,
      background: elevatedSurface,
    },
    inboxToolbar: {
      flex: '0 0 auto',
      minHeight: 84,
      padding: `${theme.spacing.md} ${horizontalPadding}`,
      background: elevatedSurface,
    },
    inlineAlert: {
      margin: `0 ${horizontalPadding} ${theme.spacing.sm}`,
    },
    messageScroll: {
      minHeight: 0,
      background: surface,
    },
    messageList: {
      minHeight: '100%',
      padding: horizontalPadding,
    },
    emptyMessages: {
      flex: 1,
      minHeight: 320,
    },
    messageColumn: {
      maxWidth: compact ? '84%' : '78%',
      minWidth: 0,
    },
    incomingMessage: {
      border: `1px solid ${border}`,
      borderBottomLeftRadius: 5,
      background: elevatedSurface,
      boxShadow: theme.shadows.xs,
    },
    outgoingMessage: {
      color: theme.white,
      borderBottomRightRadius: 2,
      background: outgoingMessageBackground,
      boxShadow: theme.shadows.xs,
    },
    messageText: {
      lineHeight: 1.5,
      overflowWrap: 'anywhere',
      whiteSpace: 'pre-wrap',
    },
    messageTime: {
      marginTop: 4,
      opacity: 0.68,
    },
    incomingAttachment: {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr) auto',
      alignItems: 'center',
      gap: 6,
      width: '100%',
      minWidth: 0,
      padding: '6px 8px',
      color: 'inherit',
      borderRadius: theme.radius.sm,
      background: primarySoft,
    },
    outgoingAttachment: {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr) auto',
      alignItems: 'center',
      gap: 6,
      width: '100%',
      minWidth: 0,
      padding: '6px 8px',
      color: 'inherit',
      borderRadius: theme.radius.sm,
      background: rgba(theme.white, 0.15),
    },
    composerArea: {
      flex: '0 0 auto',
      padding: `${theme.spacing.sm} ${horizontalPadding} ${theme.spacing.md}`,
      borderTop: `1px solid ${border}`,
      background: elevatedSurface,
    },
    composer: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: theme.spacing.xs,
      padding: `7px 8px 7px ${theme.spacing.sm}`,
      borderColor: border,
      background: elevatedSurface,
      boxShadow: theme.shadows.xs,
    },
    queuedAttachment: {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr) auto',
      alignItems: 'center',
      gap: 5,
      maxWidth: '100%',
      padding: '5px 6px 5px 8px',
      borderColor: border,
      background: surface,
    },
    conversationRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
      width: '100%',
      padding: `14px ${horizontalPadding}`,
      color: text,
      borderBottom: `1px solid ${border}`,
      background: elevatedSurface,
      transition: 'background-color 120ms ease, transform 120ms ease',
    },
    conversationSummary: {
      flex: 1,
      minWidth: 0,
      textAlign: 'left',
    },
    rowTime: {
      flex: '0 0 auto',
    },
    rowChevron: {
      flex: '0 0 auto',
      color: theme.colors[`gray-${colorScheme}`][0],
    },
    emptyInbox: {
      minHeight: 440,
      padding: theme.spacing.xl,
    },
  };
};
